import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { isIOS } from 'environment'
import { UNAVAILABLE_REASON } from 'services/share'
import VideoPlayerContainer from 'components/VideoPlayerContainer'
import SharePageV3VideoError from './SharePage.v3VideoError'
import SharePageV3VideoPlayer from './SharePage.v3VideoPlayer'
import SharePageV3VideoEnded from './SharePage.v3VideoEnded'
import SharePageV3VideoPlayCTA from './SharePage.v3VideoPlayCta'

const videoStates = {
  NOT_STARTED: 'not-started',
  ERROR: 'error',
  IS_PLAYING: 'is-playing',
  PAUSED: 'paused',
  HAS_ENDED: 'has-ended',
}

/**
 * The video component
 * @param {Object} props - The props
 * @param {Map} props.share - The share data
 * @param {Map} props.error - The error data
 * @param {Boolean} props.isAnonymous - The value indicating if user is not logged in
 * @param {Map} props.staticText - The static text
 * @param {String} props.language - The user's language
 * @returns {JSX.Element} Video component
 */

// eslint-disable-next-line react/display-name
const SharePageV3Video = forwardRef((props, ref) => {
  const {
    share,
    error,
    isAnonymous,
    staticText,
    setQualifiedView,
    language,
  } = props

  const unavailableReason = share.get('unavailableReason')

  const [videoState, setVideoState] = useState()

  const player = useRef(null)

  const isPlayerActive = [videoStates.IS_PLAYING, videoStates.PAUSED].includes(videoState)
  const isNotStartedOnIos = isIOS() && videoState === videoStates.NOT_STARTED

  const handleVideoPlayClick = () => {
    if ((!unavailableReason || unavailableReason === UNAVAILABLE_REASON.NOT_SHARABLE)
      && videoState !== videoStates.IS_PLAYING) {
      setVideoState(videoStates.IS_PLAYING)
    }
  }

  const handleVideoEnded = () => {
    setVideoState(videoStates.HAS_ENDED)
  }

  const handleVideoPaused = (playerInstance) => {
    setVideoState(videoStates.PAUSED)
    player.current = playerInstance
  }

  const handleVideoResumeClick = () => {
    if (player.current) {
      player.current.play()
    }
    setVideoState(videoStates.IS_PLAYING)
  }

  const handleVideoViewed = () => {
    const token = share.get('token')
    if (token && isAnonymous) {
      setQualifiedView(token)
    }
  }

  const handleVideoResumed = () => {
    setVideoState(videoStates.IS_PLAYING)
  }

  useImperativeHandle(ref, () => ({
    playVideo () {
      handleVideoPlayClick()
    },
  }))

  useEffect(() => {
    if (error || unavailableReason) {
      setVideoState(videoStates.ERROR)
    } else {
      setVideoState(videoStates.NOT_STARTED)
    }
  }, [error, unavailableReason])

  return (
    <VideoPlayerContainer
      isPlayerActive={isPlayerActive}
      poster={share.getIn(['content', 'imageNoText'])}
      className="share-page-v3__video-container"
    >
      {(isPlayerActive || isNotStartedOnIos) && <SharePageV3VideoPlayer
          share={share}
          language={language}
          onVideoEnded={handleVideoEnded}
          onVideoPaused={handleVideoPaused}
          onVideoViewed={handleVideoViewed}
          onVideoResumed={handleVideoResumed}
        />
      }
      {videoState === videoStates.NOT_STARTED && <SharePageV3VideoPlayCTA
          share={share}
          onClick={handleVideoPlayClick}
        />
      }
      {videoState === videoStates.ERROR && <SharePageV3VideoError
          error={error}
          share={share}
          staticText={staticText}
          onClick={handleVideoPlayClick}
        />
      }
      {videoState === videoStates.PAUSED && <SharePageV3VideoEnded
          share={share}
          staticText={staticText}
          onClick={handleVideoResumeClick}
          isAnonymous={isAnonymous}
          isPaused
        />
      }
      {videoState === videoStates.HAS_ENDED && <SharePageV3VideoEnded
          share={share}
          staticText={staticText}
          onClick={handleVideoPlayClick}
          isAnonymous={isAnonymous}
        />
      }
    </VideoPlayerContainer>
  )
})

export default SharePageV3Video
